if (!customElements.get("quantity-discount-picker")) {
  class QuantityDiscountPicker extends HTMLElement {
    constructor() {
      super();
      this.buttons = this.querySelectorAll(".quantity-button");
      this.availableQuantityOptions = [];
      this.initAvailableQuantityOptionsSorted();
    }

    get quantitySelector() {
      return document.querySelector(".product__blocks .quantity__input");
    }

    get pdpPriceWrapper() {
      return document.querySelector(
        ".product__blocks .f-price:not(.quantity-discount)",
      );
    }
    get unitPriceWrapper() {
      return document.querySelector(
        ".product__blocks .f-price__unit-wrapper:not(.quantity-discount)",
      );
    }
    get pdpDiscountPriceWrapper() {
      return document.querySelector(
        ".product__blocks .product__block--price .f-price.quantity-discount",
      );
    }
    get discountUnitPriceWrapper() {
      return document.querySelector(
        ".product__blocks .product__block--price .f-price__unit-wrapper.quantity-discount",
      );
    }

    get showSubtotal() {
      return this.dataset.showSubtotal === "true";
    }
    get selectedOption() {
      return this.buttons
        ? Array.from(this.buttons).find((b) => b.classList.contains("active"))
        : null;
    }
    get selectedQuantity() {
      return this.selectedOption
        ? parseInt(this.selectedOption.getAttribute("data-quantity") || "1")
        : 1;
    }
    get selectedDiscountPrice() {
      if (!this.selectedOption) return this.dataset.productPrice;
      return this.selectedOption.getAttribute("data-discount-price");
    }
    get selectedUnitTotalVolume() {
      return this.selectedOption
        ? parseInt(this.selectedOption.getAttribute("data-unit-total-volume"))
        : null;
    }
    get selectedUnitPrice() {
      if (!this.selectedOption) return this.dataset.productPrice;
      return this.selectedOption.getAttribute("data-unit-price");
    }
    get originalPrice() {
      const originalPrice =
        (this.dataset.plainPrice * this?.selectedQuantity) / 100;
      if (!this.selectedOption) return originalPrice + " €";
      return this.selectedOption.getAttribute("data-original-price");
    }

    connectedCallback() {
      if (this.buttons) {
        this.buttons.forEach((button) => {
          button.addEventListener("click", () => {
            this.setQuantity(button);
            this.setActive(button);
          });
        });
      }

      const input = this.quantitySelector;
      if (input) {
        input.addEventListener("change", (event) => {
          const quantity = event.target.value;

          document.dispatchEvent(
            new CustomEvent("pdp:quantity-change", {
              detail: { quantity },
            }),
          );

          if (!quantity || !this.buttons) return;

          let bestMatch;
          this.buttons.forEach((button) => {
            if (Number(quantity) >= Number(button.dataset.quantity)) {
              bestMatch = button;
            }
          });
          this.setActive(bestMatch);
        });
      }
    }

    setActive(button) {
      if (!this.buttons || !button) return;
      this.buttons.forEach((btn) => btn.classList.remove("active"));
      button.classList.add("active");

      const activeTextElement = this.querySelector("#active-quantity-text");
      if (activeTextElement) {
        activeTextElement.textContent =
          button.querySelector(".quantity-button__text")?.textContent || "";
      }

      if (!button.dataset.discount) {
        this.pdpPriceWrapper?.classList.remove("hidden");
        this.pdpDiscountPriceWrapper?.classList.add("hidden");
        if (this.selectedUnitPrice && this.selectedUnitTotalVolume) {
          this.unitPriceWrapper?.classList.remove("hidden");
          this.discountUnitPriceWrapper?.classList.add("hidden");
        }
        return;
      }

      const priceTemplate = `<div class="quantity-discount f-price f-price--large f-price--on-sale hidden">
            <div class="f-price__regular">
              <span class="visually-hidden visually-hidden--inline">Regulärer Preis</span>
              <span class="f-price-item f-price-item--regular">${this.dataset.productPrice}</span>
            </div>
            <div class="f-price__sale">
              <span class="visually-hidden visually-hidden--inline">Verkaufspreis</span>
              <span class="f-price-item f-price-item--sale">${this.selectedDiscountPrice}</span>
              <span class="visually-hidden visually-hidden--inline">Regulärer Preis</span>
              <span class="f-price-item f-price-item--regular"><s>${this.originalPrice}</s></span>
            </div>
          </div>`;

      this.updatePriceWrapper(
        this.pdpPriceWrapper,
        this.pdpDiscountPriceWrapper,
        priceTemplate,
        ".f-price-item--sale",
        this.selectedDiscountPrice,
      );

      this.updatePriceWrapper(
        this.pdpPriceWrapper,
        this.pdpDiscountPriceWrapper,
        priceTemplate,
        ".f-price-item--regular s",
        this.originalPrice,
      );

      if (this.selectedUnitPrice && this.selectedUnitTotalVolume) {
        const unitTemplate = `<div class="f-price__unit-wrapper quantity-discount">
              <span class="visually-hidden">Stückpreis</span>
              <div class="f-price__unit">
                <span data-value-volume=""></span>
                <span data-unit-volume=""></span> -
                <span data-unit-price=""></span>
                <span aria-hidden="true">/</span>
                <span class="visually-hidden">pro&nbsp;</span>
                <span data-unit-price-base-unit=""></span>
              </div>
            </div>`;

        this.updatePriceWrapper(
          this.unitPriceWrapper,
          this.discountUnitPriceWrapper,
          unitTemplate,
          "span[data-unit-price]",
          this.selectedUnitPrice,
        );

        this.updatePriceWrapper(
          this.unitPriceWrapper,
          this.discountUnitPriceWrapper,
          unitTemplate,
          "span[data-value-volume]",
          this.selectedUnitTotalVolume,
        );
      }
    }

    updatePriceWrapper(
      originalWrapper,
      discountWrapper,
      innerHTML,
      priceSelector,
      price,
    ) {
      if (originalWrapper && !discountWrapper) {
        this.renderInnerHTML(originalWrapper, innerHTML);
      }
      originalWrapper?.classList.add("hidden");
      discountWrapper?.classList.remove("hidden");

      const refreshedDiscountWrapper =
        discountWrapper || this.pdpDiscountPriceWrapper;
      const existingDiscountElements =
        refreshedDiscountWrapper?.querySelectorAll(priceSelector);
      existingDiscountElements?.forEach((el) => {
        el.innerHTML = price;
      });
    }

    setQuantity(button) {
      const input = this.quantitySelector;
      if (!input) return;
      const quantity = button.dataset.quantity;
      if (!quantity) return;

      input.value = quantity;
      input.dispatchEvent(new Event("input", { bubbles: true }));
      input.dispatchEvent(new Event("change", { bubbles: true }));
    }

    initAvailableQuantityOptionsSorted() {
      const quantityOptions = this.buttons;
      if (!quantityOptions) {
        return null;
      }

      quantityOptions.forEach((option) => {
        const optionQuantity = parseInt(
          option.getAttribute("data-quantity"),
          10,
        );
        this.availableQuantityOptions.push(optionQuantity);
      });

      this.availableQuantityOptions.sort((a, b) => a - b);
    }

    getProductDiscountPricesByQuantity(quantity) {
      for (let i = this.availableQuantityOptions.length - 1; i >= 0; i--) {
        const optionQuantity = this.availableQuantityOptions[i];
        const nextOptionQuantity = this.availableQuantityOptions[i + 1];

        if (
          quantity >= optionQuantity &&
          (!nextOptionQuantity || quantity < nextOptionQuantity)
        ) {
          const matchingButton = Array.from(this.buttons).find(
            (btn) =>
              parseInt(btn.getAttribute("data-quantity"), 10) ===
              optionQuantity,
          );
          if (!matchingButton) {
            break;
          }
          return {
            originalPrice: matchingButton.dataset.singleCompareAtPrice,
            discountPrice: matchingButton.dataset.discountedSinglePrice,
            unitPrice: matchingButton.dataset.unitPrice,
            discount: parseFloat(matchingButton.dataset.discount) || 0,
            text:
              matchingButton
                .querySelector(".quantity-button__text")
                ?.textContent?.trim() || "",
          };
        }
      }
    }

    renderInnerHTML(targetElement, innerHTML) {
      const newElement = document.createElement("div");
      newElement.innerHTML = innerHTML;
      targetElement.insertAdjacentHTML("afterend", newElement.innerHTML);
    }
  }
  customElements.define("quantity-discount-picker", QuantityDiscountPicker);
}
